import { supabase } from './supabase'

/**
 * Save an examination template (questionnaire or charting)
 */
export const saveExaminationTemplate = async (organizationId, templateData) => {
  try {
    // Store type in name prefix or use custom_questions_template for questionnaire type
    const templatePayload = {
      organization_id: organizationId,
      name: templateData.name,
      description: templateData.description || null,
      updated_at: new Date().toISOString(),
    }

    // For questionnaire templates, store in custom_questions_template
    if (templateData.type === 'questionnaire') {
      templatePayload.custom_questions_template = templateData.templateData || []
      templatePayload.sections = {} // Empty sections for questionnaire-only templates
    } else {
      // For charting or full templates, store in sections
      templatePayload.sections = templateData.templateData || {}
      templatePayload.custom_questions_template = []
    }

    const { data, error } = await supabase
      .from('examination_templates')
      .upsert(templatePayload, {
        onConflict: 'id',
      })
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error saving examination template:', error)
    throw error
  }
}

/**
 * Get all examination templates for an organization
 * Filters by type: 'questionnaire' (has custom_questions_template) or 'charting' (has sections)
 */
export const getExaminationTemplates = async (organizationId, type = null) => {
  try {
    let query = supabase
      .from('examination_templates')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })

    const { data, error } = await query

    if (error) throw error

    // Filter by type if specified
    let filteredData = data || []
    if (type === 'questionnaire') {
      // Questionnaire templates have custom_questions_template with data
      filteredData = filteredData.filter(
        (t) => t.custom_questions_template && Array.isArray(t.custom_questions_template) && t.custom_questions_template.length > 0
      )
    } else if (type === 'charting') {
      // Charting templates have sections with data
      filteredData = filteredData.filter(
        (t) => t.sections && typeof t.sections === 'object' && Object.keys(t.sections).length > 0
      )
    }

    return { success: true, data: filteredData }
  } catch (error) {
    console.error('Error fetching examination templates:', error)
    throw error
  }
}

/**
 * Delete an examination template
 */
export const deleteExaminationTemplate = async (templateId) => {
  try {
    const { error } = await supabase.from('examination_templates').delete().eq('id', templateId)

    if (error) throw error
    return { success: true }
  } catch (error) {
    console.error('Error deleting examination template:', error)
    throw error
  }
}

/**
 * Get a single examination template by ID
 */
export const getExaminationTemplate = async (templateId) => {
  try {
    const { data, error } = await supabase
      .from('examination_templates')
      .select('*')
      .eq('id', templateId)
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error fetching examination template:', error)
    throw error
  }
}
